"use client";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { useTenant } from "./TenantContext";
import type { LanguageType } from "@/services/language";
import { applyBusinessLabels } from "@/lib/businessLabels";

import en from "@/locales/en.json";
import hindi from "@/locales/hindi.json";
import marathi from "@/locales/marathi.json";

const locales: Record<LanguageType, Record<string, string>> = { english: en, hindi, marathi };

const LANG_TO_HTML: Record<LanguageType, string> = { english: "en", hindi: "hi", marathi: "mr" };

interface LanguageContextType {
  language: LanguageType;
  setLanguage: (lang: LanguageType) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  /**
   * Business-type-aware translation. Runs `t(key, params)` then swaps domain
   * nouns ("parking" -> "cloak room", "vehicle" -> "item") based on the current
   * tenant's businessType. Use for customer-facing strings that should change
   * label per business type. Falls back to `t()` when no tenant is loaded.
   */
  tb: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLangState] = useState<LanguageType>(() => {
    if (typeof window === "undefined") return "english";
    const cached = localStorage.getItem("language-preference");
    if (cached === "english" || cached === "hindi" || cached === "marathi") return cached;
    return "english";
  });

  const { tenantData } = useTenant();

  // Sync with tenant setting on first load (if no user preference stored)
  useEffect(() => {
    const cached = localStorage.getItem("language-preference");
    if (!cached && tenantData?.settings?.language) {
      const tenantLang = tenantData.settings.language as LanguageType;
      if (tenantLang !== language) {
        setLangState(tenantLang);
      }
    }
  }, [tenantData]);

  // Update <html lang> and persist
  useEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.lang = LANG_TO_HTML[language];
      localStorage.setItem("language-preference", language);
    }
  }, [language]);

  const setLanguage = useCallback(async (lang: LanguageType) => {
    setLangState(lang);
    localStorage.setItem("language-preference", lang);
    document.documentElement.lang = LANG_TO_HTML[lang];
    try {
      const { updateTenantLanguage } = await import("@/services/language");
      await updateTenantLanguage(lang);
    } catch {
      // Silently fail — user already has the local preference saved
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let value = locales[language]?.[key] ?? locales.english[key] ?? key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), String(v));
        });
      }
      return value;
    },
    [language]
  );

  const businessType = tenantData?.tenant?.businessType ?? "PARKING";

  const tb = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      return applyBusinessLabels(t(key, params), businessType);
    },
    [t, businessType]
  );

  const value = useMemo<LanguageContextType>(
    () => ({ language, setLanguage, t, tb }),
    [language, setLanguage, t, tb]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
